import React, { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Input, Form, Checkbox } from 'components/Forms'
import { closeModal } from 'store/actions/modals'
import { Alert } from 'ui'
import { getUserName } from 'services/Utilities'
import { refreshCounter } from 'store/actions/refresh'
import { createErrorNotification } from 'store/actions/notifications'
import { omitBy } from 'lodash'
import { Card } from 'ui'

import { t } from 'translations'

const UserArchiveModal = ({ userId }) => {
  const dispatch = useDispatch()

  const [affectedElements, setAffectedElements] = useState({})

  const fullName = getUserName(userId)

  // Fetch the count of elements that will be affected
  useEffect(() => {
    const loadAffectedElements = async () => {
      const result = await Api.put(`/users/archive/archive`, { id: userId, getAffectedElements: true })
      if (!result) return dispatch(createErrorNotification())
      // Remove null values
      const cleanedResult = omitBy(result, value => value === 0)
      setAffectedElements(cleanedResult)
    }
    loadAffectedElements()
  }, [dispatch, userId])

  const submit = async actions => {
    const { success } = await Api.put('/users/archive/archive', { id: userId })
    if (!success) {
      actions.setSubmitting(false)
      actions.setStatus({ type: 'danger', msg: 'manager.users.archiveModal.ERROR_ARCHIVING' })
    } else {
      dispatch(refreshCounter('users'))
      dispatch(closeModal())
    }
  }

  return (
    <BaseModal hideButtons title="manager.users.archiveModal" titleVar={{ fullName }}>
      <Formik
        enableReinitialize={true}
        validationSchema={Yup.object().shape({
          confirm: Yup.mixed()
            .test(
              'ARCHIVE',
              t('manager.users.archiveUsersModal.BAD_MATCH'),
              value => value?.toLowerCase() === t('manager.users.archiveUsersModal.CONFIRM.WORD')
            )
            .required('manager.users.archiveUsersModal.FIELD_IS_REQUIRED')
        })}
        initialValues={{ confirm: '' }}
        onSubmit={submit}
      >
        {({ status, isSubmitting, setStatus, values }) => {
          const canArchive =
            Object.keys(affectedElements).every(resource => values[resource]) &&
            values.confirm.toLowerCase() === t('manager.users.archiveUsersModal.CONFIRM.WORD')

          return (
            <Form className="mt-2">
              {!!Object.keys(affectedElements).length && (
                <Card className="bg-light my-2">
                  <Card.Body>
                    <Trans t={t} i18nKey={'administration.confirmDeletion.WILL_BE_IMPACTED'} components={{ h6: <h6 /> }} />
                    <ul className="mt-3">
                      {Object.keys(affectedElements).map(resourceName => {
                        return (
                          <Checkbox
                            key={`${resourceName}`}
                            name={`${resourceName}`}
                            noDescription
                            label={' '}
                            required
                            customLabelElement={
                              <Trans t={t} count={affectedElements[resourceName]} i18nKey={`manager.users.archiveModal.${resourceName.toUpperCase()}`}>
                                <b></b>
                              </Trans>
                            }
                          />
                        )
                      })}
                    </ul>
                  </Card.Body>
                </Card>
              )}

              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <div className="mb-3"></div>
              <Input module={`manager.users.archiveUsersModal`} name="confirm" required />
              <Button className="btn btn-danger mr-2" type="submit" isSubmitting={isSubmitting} disabled={!canArchive}>
                {t(`manager.users.archiveModal.ARCHIVE_USER`, {
                  fullName
                })}
              </Button>
              <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                {t(`actions.CLOSE`)}
              </Button>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default UserArchiveModal
